import { createContext, FC, useContext, type ReactNode } from "react";
import { PERMISSIONS } from "../../constants/permissionKeys";
import { useAlert } from "../../hooks/useAlert";
import { useHasPermission } from "../../hooks/useHasPermission";
import { useNotificationInbox } from "./useNotificationInbox";
import type { NotificationInboxVariant } from "./useNotificationInbox";

export interface ShellNotificationsApi {
  inboxOpen: boolean;
  openInbox: () => void;
  closeInbox: () => void;
  inboxTitle: ReactNode;
  renderTrigger: (variant: NotificationInboxVariant) => ReactNode;
  renderInboxContent: () => ReactNode;
  unreadCount: number;
}

export interface ShellNotificationsProviderProps {
  children: ReactNode;
  showSuccess?: (msg: string) => void;
  showError?: (msg: string) => void;
}

const ShellNotificationsContext = createContext<ShellNotificationsApi | null>(null);

/**
 * 整个 HomeShell 共享一份通知收件箱实例：
 * 侧栏通知入口、顶栏通知入口、通知弹窗读取同一个状态，避免未读数/分页不一致。
 */
export const ShellNotificationsProvider: FC<ShellNotificationsProviderProps> = ({
  children,
  showSuccess,
  showError,
}) => {
  const fallback = useAlert();
  const hasNotify = useHasPermission(PERMISSIONS.NOTIFICATION_VIEW);
  const inbox = useNotificationInbox({
    showSuccess: showSuccess ?? fallback.showSuccess,
    showError: showError ?? fallback.showError,
    enabled: hasNotify === true,
  });
  return (
    <ShellNotificationsContext.Provider value={inbox}>
      {children}
    </ShellNotificationsContext.Provider>
  );
};

export function useShellNotifications(): ShellNotificationsApi | null {
  return useContext(ShellNotificationsContext);
}
